<template>
  <div>
    <a-page-header title="AI调用记录">
      <template #extra>
        <a-button type="primary" @click="() => $router.push({ name: 'execution-plan-statistics' })">
          <template #icon><BarChartOutlined /></template>
          统计报表
        </a-button>
      </template>
    </a-page-header>

    <a-card style="margin-top: 16px">
      <a-form :model="searchForm" style="margin-bottom: 16px">
        <a-row :gutter="16">
          <a-col :xs="24" :sm="12" :md="8" :lg="6" :xl="6">
            <a-form-item label="搜索">
              <a-input
                v-model:value="searchForm.keyword"
                placeholder="搜索 task_id、user_id、方案名称、故障现象"
                allowClear
                @input="handleSearchInput"
              >
                <template #prefix>
                  <SearchOutlined />
                </template>
              </a-input>
            </a-form-item>
          </a-col>
          <a-col :xs="24" :sm="12" :md="6" :lg="4" :xl="4">
            <a-form-item label="状态">
              <a-select v-model:value="searchForm.status" @change="handleSearch">
                <a-select-option value="">全部</a-select-option>
                <a-select-option value="pending">待执行</a-select-option>
                <a-select-option value="executing">执行中</a-select-option>
                <a-select-option value="completed">已完成</a-select-option>
                <a-select-option value="cancelled">已取消</a-select-option>
                <a-select-option value="failed">失败</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :xs="24" :sm="12" :md="6" :lg="4" :xl="4">
            <a-form-item label="创建时间">
              <a-select 
                v-model:value="timeRange" 
                @change="onTimeRangeChange"
              >
                <a-select-option value="today">今天</a-select-option>
                <a-select-option value="last3days">最近3天</a-select-option>
                <a-select-option value="last7days">最近7天</a-select-option>
                <a-select-option value="last30days">最近一个月</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :xs="24" :sm="12" :md="6" :lg="4" :xl="4">
            <a-form-item label="标签筛选">
              <a-select
                v-model:value="searchForm.tagIds"
                mode="multiple"
                placeholder="选择标签"
                :options="tagOptions"
                @change="handleTagChange"
              />
            </a-form-item>
          </a-col>
          <a-col :xs="24" :sm="12" :md="4" :lg="4" :xl="4">
            <a-form-item>
              <a-button type="primary" @click="handleSearch">查询</a-button>
              <a-button style="margin-left: 8px" @click="handleReset">重置</a-button>
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>

      <a-table
        :columns="columns"
        :data-source="dataSource"
        :loading="loading"
        :pagination="pagination"
        row-key="id"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'system_display'">
            <template v-if="record.system_display && record.system_display !== '-'">
              <div v-for="(line, idx) in record.system_display.split('\n')" :key="idx">{{ line }}</div>
            </template>
            <span v-else>-</span>
          </template>
          <template v-else-if="column.key === 'status'">
            <a-tag :color="getStatusColor(record.status, record.feedback_status)">
              {{ getStatusText(record.status, record.feedback_status) }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'tags'">
            <div class="tags-container">
              <a-tag v-for="tag in record.planTags" :key="tag.id" :color="tag.tag_color">
                {{ tag.tag_name }}
              </a-tag>
              <a-button 
                v-if="record.planTags && record.planTags.length > 0"
                type="text" 
                size="small"
                @click="showManageTagsModal(record)"
                style="color: #1890ff; font-size: 12px;"
              >
                管理
              </a-button>
            </div>
          </template>
          <template v-else-if="column.key === 'fault_phenomenon'">
            <template v-if="record.matched_fault_phenomenon">
              <div>{{ record.fault_phenomenon }}</div>
              <div style="font-size:12px; color:#666;">
                → {{ record.matched_fault_phenomenon }}
                <a-tag color="blue" style="font-size:11px; margin-left:4px; line-height:1.4">语义匹配</a-tag>
              </div>
            </template>
            <span v-else>{{ record.fault_phenomenon || '-' }}</span>
          </template>
          <template v-else-if="column.key === 'create_time'">
            <span v-if="record.create_time">{{ formatTime(record.create_time) }}</span>
            <span v-else style="color: red;">[无数据]</span>
          </template>
          <template v-else-if="column.key === 'action'">
            <a-button type="link" @click="handleView(record)">查看详情</a-button>
            <a-divider type="vertical" />
            <a-button type="link" @click="showManageTagsModal(record)">打标签</a-button>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 标签管理弹窗 -->
    <a-modal
      v-model:visible="tagModalVisible"
      title="管理执行计划标签"
      @ok="handleSaveTags"
      :confirm-loading="tagModalLoading"
      width="600px"
    >
      <a-space direction="vertical" style="width: 100%">
        <div>
          <p style="margin-bottom: 8px;">已关联标签：</p>
          <div class="tag-list">
            <a-tag
              v-for="tag in currentPlanTags"
              :key="tag.id"
              :color="tag.tag_color"
              closable
              @close="handleRemoveTag(tag.id)"
            >
              {{ tag.tag_name }}
            </a-tag>
          </div>
        </div>
        
        <div>
          <p style="margin-bottom: 8px;">可选标签：</p>
          <a-select
            v-model:value="selectedTagIds"
            mode="multiple"
            placeholder="选择要添加的标签"
            :options="availableTagOptions"
            style="width: 100%"
          />
          <a-button 
            type="primary" 
            style="margin-top: 8px"
            @click="handleAddTags"
            :loading="tagModalLoading"
          >
            添加标签
          </a-button>
        </div>

        <a-divider />
        
        <div>
          <p style="margin-bottom: 8px;">创建新标签：</p>
          <a-input-group compact style="width: 100%">
            <a-input
              v-model:value="newTagName"
              placeholder="输入新标签名称"
              style="width: calc(100% - 100px)"
            />
            <a-button type="primary" @click="handleCreateNewTag" :loading="tagModalLoading">
              创建
            </a-button>
          </a-input-group>
        </div>
      </a-space>
    </a-modal>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import { SearchOutlined, BarChartOutlined } from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { getCallPlans } from '../api/callPlan';
import { 
  getAllTags, 
  getPlanTags, 
  addTagToPlan, 
  removeTagFromPlan,
  createTag 
} from '../api/executionPlanTag';

// 启用 dayjs utc 插件
dayjs.extend(utc);

export default {
  name: 'ExecutionPlanList',
  components: {
    SearchOutlined,
    BarChartOutlined
  },
  setup() {
    const router = useRouter();
    const loading = ref(false);
    const dataSource = ref([]);
    const timeRange = ref('today'); // 默认选择"今天"
    const searchForm = reactive({
      keyword: undefined, // 统一搜索关键词
      status: '', // 默认值"全部"（空字符串）
      tagIds: [], // 标签筛选
      start_time_from: undefined,
      start_time_to: undefined
    });
    const pagination = reactive({
      current: 1,
      pageSize: 20,
      total: 0
    });

    // 标签相关状态
    const allTags = ref([]);
    const tagModalVisible = ref(false);
    const tagModalLoading = ref(false);
    const currentPlanId = ref(null);
    const currentPlanTags = ref([]);
    const selectedTagIds = ref([]);
    const newTagName = ref('');

    // 防抖定时器
    let searchTimer = null;

    // columns 定义为常量数组，保持引用稳定
    const columns = [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
      { title: '方案名称', dataIndex: 'scheme_name', key: 'scheme_name', width: 200 },
      { title: 'task_id', dataIndex: 'task_id', key: 'task_id', width: 200 },
      { title: 'user_id', dataIndex: 'user_id', key: 'user_id', width: 100 },
      { title: '故障现象', key: 'fault_phenomenon', width: 200 },
      { title: 'SYSTEMID&Name', key: 'system_display', width: 200 },
      { title: '标签', key: 'tags', width: 200 },
      { title: '状态', key: 'status', width: 100 },
      { title: '创建时间', dataIndex: 'create_time', key: 'create_time', width: 180 },
      { title: '操作', key: 'action', width: 200, fixed: 'right' }
    ];

    // 计算标签选项
    const tagOptions = computed(() => allTags.value.map(tag => ({
      label: tag.tag_name,
      value: tag.id
    })));

    // 计算可用标签选项（排除已关联的）
    const availableTagOptions = computed(() => allTags.value
      .filter(tag => !currentPlanTags.value.some(t => t.id === tag.id))
      .map(tag => ({
        label: tag.tag_name,
        value: tag.id,
        color: tag.tag_color
      })));

    const getStatusColor = (status, feedbackStatus) => {
      // 如果是已完成状态且有反馈状态，根据反馈状态返回不同颜色
      if (status === 'completed' && feedbackStatus) {
        if (feedbackStatus === '未反馈') {
          return 'gold'; // 淡黄色
        } else if (feedbackStatus === '有帮助') {
          return 'green'; // 淡绿色（保持原色）
        } else if (feedbackStatus === '没帮助') {
          return 'red'; // 淡红色
        }
      }
      
      // 其他状态使用默认颜色
      const colors = {
        pending: 'orange',
        executing: 'blue',
        completed: 'green',
        cancelled: 'default',
        failed: 'red'
      };
      return colors[status] || 'default';
    };

    const getStatusText = (status, feedbackStatus) => {
      const texts = {
        pending: '待执行',
        executing: '执行中',
        completed: '已完成',
        cancelled: '已取消',
        failed: '失败'
      };
      const baseText = texts[status] || status;
      
      // 如果是已完成状态且有反馈状态，显示反馈状态
      if (status === 'completed' && feedbackStatus) {
        return `${baseText}/${feedbackStatus}`;
      }
      
      return baseText;
    };

    // 格式化时间为北京时间 (YYYY-MM-DD HH:mm:ss)
    const formatTime = (time) => {
      if (!time) {
        return '-';
      }
      try {
        let parsedTime;
        
        // 判断时间格式并转换为北京时间
        if (typeof time === 'string') {
          // 如果包含 'Z'，说明是 UTC 时间（ISO 8601 格式，如：2026-01-06T08:12:55.000Z）
          if (time.includes('Z')) {
            // 解析为 UTC 时间，然后加 8 小时转换为北京时间（UTC+8）
            parsedTime = dayjs.utc(time).add(8, 'hour');
          }
          // 如果包含时区偏移（如 +08:00, -05:00）
          else if (time.match(/[+-]\d{2}:\d{2}$/)) {
            // 解析时间字符串，提取时区偏移
            const timeStr = time.replace(/[+-]\d{2}:\d{2}$/, '');
            const offsetMatch = time.match(/([+-])(\d{2}):(\d{2})$/);
            if (offsetMatch) {
              const sign = offsetMatch[1] === '+' ? 1 : -1;
              const hours = parseInt(offsetMatch[2], 10);
              const minutes = parseInt(offsetMatch[3], 10);
              const offsetHours = sign * (hours + minutes / 60);
              // 转换为 UTC，然后加 8 小时得到北京时间
              parsedTime = dayjs.utc(timeStr).add(8 - offsetHours, 'hour');
            } else {
              parsedTime = dayjs(time);
            }
          }
          // 如果是 MySQL DATETIME 格式（如：2026-01-06 16:12:55）
          // 假设数据库返回的时间已经是北京时间，直接解析
          else {
            parsedTime = dayjs(time);
          }
        } else if (time instanceof Date) {
          // 如果是 Date 对象，它存储的是 UTC 时间，需要转换为北京时间
          parsedTime = dayjs.utc(time).add(8, 'hour');
        } else {
          // 其他格式，尝试直接解析
          parsedTime = dayjs(time);
        }
        
        if (!parsedTime || !parsedTime.isValid()) {
          return '-';
        }
        
        // 格式化为北京时间
        return parsedTime.format('YYYY-MM-DD HH:mm:ss');
      } catch (error) {
        console.error('时间格式化错误:', error, '原始值:', time);
        return '-';
      }
    };

    // 加载标签列表
    const loadTags = async () => {
      try {
        const res = await getAllTags();
        if (res.code === 200) {
          allTags.value = res.data || [];
        }
      } catch (error) {
        console.error('加载标签失败:', error);
      }
    };

    // 加载数据
    const loadData = async () => {
      loading.value = true;
      try {
        const params = {
          ...searchForm,
          page: pagination.current,
          pageSize: pagination.pageSize
        };
        // 如果status为空字符串，不传status参数（表示查询全部）
        if (params.status === '') {
          delete params.status;
        }
        const res = await getCallPlans(params);
        if (res.code === 200) {
          // 后端返回格式：{ code: 200, data: { items: [...], total: ... } }
          const items = res.data?.items || [];
          // 后端现在已经在 getList 中通过子查询返回了 planTags，无需前端循环请求
          dataSource.value = items.map(item => ({
            ...item,
            id: item.id || item.plan_id || Math.random(),
            planTags: item.planTags || []
          }));
          pagination.total = res.data?.total || 0;
        } else {
          message.error(res.message || '加载数据失败');
          dataSource.value = [];
        }
      } catch (error) {
        message.error('加载数据失败');
        console.error(error);
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      pagination.current = 1;
      loadData();
    };

    const handleSearchInput = () => {
      // 防抖处理：300ms后触发搜索
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
      searchTimer = setTimeout(() => {
        handleSearch();
      }, 300);
    };

    const handleReset = () => {
      searchForm.keyword = undefined;
      searchForm.status = ''; // 重置为"全部"
      searchForm.tagIds = []; // 重置标签筛选
      timeRange.value = 'today'; // 重置为默认值"今天"
      handleTimeRangeChange('today'); // 重新计算时间范围
      pagination.current = 1;
      loadData();
    };

    const onTimeRangeChange = (value) => {
      handleTimeRangeChange(value);
      handleSearch();
    };

    const handleTimeRangeChange = (value) => {
      if (!value) {
        searchForm.start_time_from = undefined;
        searchForm.start_time_to = undefined;
        return;
      }

      const now = dayjs();
      const endTime = now.endOf('day'); // 结束时间：今天的23:59:59
      let startTime;

      switch (value) {
        case 'today':
          startTime = now.startOf('day'); // 开始时间：今天的00:00:00
          break;
        case 'last3days':
          startTime = now.subtract(2, 'day').startOf('day'); // 3天前（包含今天）
          break;
        case 'last7days':
          startTime = now.subtract(6, 'day').startOf('day'); // 7天前（包含今天）
          break;
        case 'last30days':
          startTime = now.subtract(29, 'day').startOf('day'); // 30天前（包含今天）
          break;
        default:
          startTime = now.startOf('day');
      }

      searchForm.start_time_from = startTime.format('YYYY-MM-DD HH:mm:ss');
      searchForm.start_time_to = endTime.format('YYYY-MM-DD HH:mm:ss');
    };

    const handleTagChange = () => {
      // 标签选项改变时，触发搜索
      pagination.current = 1;
      loadData();
    };

    const handleTableChange = (pag) => {
      pagination.current = pag.current;
      pagination.pageSize = pag.pageSize;
      loadData();
    };

    const handleView = (record) => {
      // 新标签页打开详情页
      const routeData = router.resolve({
        name: 'execution-plan-detail',
        params: { id: record.id }
      });
      window.open(routeData.href, '_blank');
    };

    // 显示标签管理弹窗
    const showManageTagsModal = async (record) => {
      currentPlanId.value = record.id;
      tagModalVisible.value = true;
      selectedTagIds.value = [];
      newTagName.value = '';
      
      try {
        const res = await getPlanTags(record.id);
        if (res.code === 200) {
          currentPlanTags.value = res.data || [];
        }
      } catch (error) {
        message.error('加载标签失败');
      }
    };

    // 添加标签
    const handleAddTags = async () => {
      if (selectedTagIds.value.length === 0) {
        message.warning('请先选择要添加的标签');
        return false;
      }

      tagModalLoading.value = true;
      try {
        const addedCount = selectedTagIds.value.length;
        for (const tagId of selectedTagIds.value) {
          await addTagToPlan(currentPlanId.value, tagId);
        }
        message.success(`成功添加 ${addedCount} 个标签`);
        selectedTagIds.value = [];
        
        // 重新加载当前计划的标签
        const res = await getPlanTags(currentPlanId.value);
        if (res.code === 200) {
          currentPlanTags.value = res.data || [];
        }
        
        // 重新加载列表数据以更新标签显示
        await loadData();
        return true;
      } catch (error) {
        message.error('添加标签失败: ' + error.message);
        return false;
      } finally {
        tagModalLoading.value = false;
      }
    };

    // 移除标签
    const handleRemoveTag = async (tagId) => {
      try {
        await removeTagFromPlan(currentPlanId.value, tagId);
        message.success('标签已移除');
        
        // 重新加载当前计划的标签
        const res = await getPlanTags(currentPlanId.value);
        if (res.code === 200) {
          currentPlanTags.value = res.data || [];
        }
        
        // 重新加载列表数据以更新标签显示
        loadData();
      } catch (error) {
        message.error('移除标签失败: ' + error.message);
      }
    };

    // 创建新标签
    const handleCreateNewTag = async () => {
      if (!newTagName.value.trim()) {
        message.warning('请输入标签名称');
        return;
      }

      tagModalLoading.value = true;
      try {
        const res = await createTag({
          tag_name: newTagName.value.trim(),
          tag_color: 'default',
          description: ''
        });
        
        if (res.code === 201) {
          message.success('标签创建成功');
          newTagName.value = '';
          
          // 重新加载标签列表
          await loadTags();
          
          // 自动添加到当前计划
          const newTag = res.data;
          await addTagToPlan(currentPlanId.value, newTag.id);
          
          // 重新加载当前计划的标签
          const tagRes = await getPlanTags(currentPlanId.value);
          if (tagRes.code === 200) {
            currentPlanTags.value = tagRes.data || [];
          }
          
          // 重新加载列表数据
          loadData();
        }
      } catch (error) {
        message.error('创建标签失败: ' + error.message);
      } finally {
        tagModalLoading.value = false;
      }
    };

    // 保存标签（弹窗确定按钮）
    const handleSaveTags = async () => {
      if (selectedTagIds.value.length > 0) {
        const saved = await handleAddTags();
        if (!saved) {
          return;
        }
      }
      tagModalVisible.value = false;
    };

    onMounted(() => {
      // 初始化时间范围（默认今天）
      handleTimeRangeChange('today');
      loadTags(); // 加载标签列表
      loadData();
    });

    return {
      loading,
      dataSource,
      searchForm,
      pagination,
      timeRange,
      columns,
      allTags,
      tagModalVisible,
      tagModalLoading,
      currentPlanTags,
      selectedTagIds,
      newTagName,
      tagOptions,
      availableTagOptions,
      getStatusColor,
      getStatusText,
      formatTime,
      handleSearch,
      handleSearchInput,
      handleReset,
      handleTimeRangeChange,
      onTimeRangeChange,
      handleTagChange,
      handleTableChange,
      handleView,
      showManageTagsModal,
      handleAddTags,
      handleRemoveTag,
      handleCreateNewTag,
      handleSaveTags
    };
  }
};
</script>

<style scoped>
.tags-container {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.tag-list {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px;
  background-color: #f5f5f5;
  border-radius: 4px;
  min-height: 40px;
  align-items: center;
}
</style>
